// PromotionScreen.tsx
import { useEffect, useState, useMemo, useCallback } from 'react'
import { Breadcrumb, Button, Card, Col, Divider, Input, Pagination, Row, Table, Typography } from 'antd'
import {
   HomeOutlined,
   PlusCircleOutlined,
   EditOutlined,
   DeleteOutlined,
   ExclamationCircleOutlined,
   SearchOutlined
} from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { ROUTER_PATH } from '../routes/metaData'
import useModal from 'src/helpers/modal/useModal'
import { formatCurrency } from 'src/helpers/functions'
import { DATE_TIME_FORMAT_CLIENT, getPage, PAGE_SIZE, TYPE_PROMOTION_CODE } from 'src/helpers/constants'
import { isEmpty, size, trim } from 'lodash'
import { useLoading } from 'src/common/components/LoadingContainer'
import { useStore } from 'src/hooks'
import moment from 'moment'
const { Title } = Typography

const PromotionScreen = observer(() => {
   const history = useHistory()
   const { t } = useTranslation()
   const { confirm } = useModal()
   const UserStore = useStore('UserStore')
   const { rows, offset, count } = UserStore.listPromotion
   const [textSearch, set_textSearch] = useState('')
   const { showLoading, hideLoading } = useLoading()
   const [fieldFilter, setFieldFilter] = useState<any>({})

   useEffect(() => {
      if (size(trim(textSearch)) > 0) {
         getListData(0, { ...fieldFilter, ...{ nameSearch: trim(textSearch) } })
      } else {
         getListData(0, { ...fieldFilter })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [textSearch, fieldFilter])

   const getListData = async (currentPage: number, param?: any) => {
      try {
         showLoading()
         const offset = getPage(currentPage)
         const params = { offset, ...param }
         // console.log('params', params)

         await UserStore.getPromotion(params)
      } catch (error) {
         // console.log(error)
      } finally {
         hideLoading()
      }
   }

   const onChange = (currentPage: number) => {
      if (size(trim(textSearch)) > 0) {
         getListData(currentPage - 1, { ...fieldFilter, ...{ nameSearch: trim(textSearch) } })
      } else {
         getListData(currentPage - 1, { ...fieldFilter })
      }
   }

   const handleChangeTextSearch = (value: any) => {
      set_textSearch(value)
   }
   const handleRedirectToCreate = useCallback(() => {
      history.push(`/${ROUTER_PATH.PromotionCreateScreen}`)
   }, [history])

   const showDetail = useCallback(
      (detail: any) => {
         history.push(`/${ROUTER_PATH.PromotionDetailScreen.replace(':id', detail?.id?.toString())}`)
      },
      [history]
   )

   const showEdit = useCallback(
      (detail: any) => {
         UserStore.setPromotionSelected(detail)
         history.push(`/${ROUTER_PATH.PromotionUpdateScreen.replace(':id', detail?.id?.toString())}`)
      },
      [history]
   )

   const handleDelete = useCallback(
      (value: any) => {
         confirm({
            title: t('delete_confirm', { name: value?.name || '', code: value?.code || '' }),
            icon: <ExclamationCircleOutlined />,
            okText: t('agree'),
            cancelText: t('cancel'),
            async onOk() {
               await UserStore.deletePromotion(value?.id)
               if (size(trim(textSearch)) > 0) {
                  getListData(0, { ...fieldFilter, ...{ nameSearch: trim(textSearch) } })
               } else {
                  getListData(0, { ...fieldFilter })
               }
            }
         })
      },
      [confirm, t]
   )

   const columns = useMemo(
      () => [
         { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
         { title: 'Tiêu đề', dataIndex: 'name', key: 'name' },
         { title: 'Giá trị', dataIndex: 'value', key: 'value', render: (val: any) => formatCurrency(val) },
         {
            title: 'Loại',
            dataIndex: 'type',
            key: 'type',
            render: (val: any) => (val == TYPE_PROMOTION_CODE.FIXED ? 'Số tiền' : 'Phần trăm')
         },
         {
            title: 'Max tiền',
            dataIndex: 'maxDiscountAmount',
            key: 'maxDiscountAmount',
            render: (val: any) => formatCurrency(val)
         },
         { title: 'Lượt sử dụng', dataIndex: 'usedCount', key: 'usedCount' },
         {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (val: any) => moment(val).format(DATE_TIME_FORMAT_CLIENT)
         },
         {
            title: 'Ngày bắt đầu',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (val: any) => moment(val).format(DATE_TIME_FORMAT_CLIENT)
         },
         {
            title: 'Ngày kết thúc',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (val: any) => moment(val).format(DATE_TIME_FORMAT_CLIENT)
         },
         {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (val: any) => (val ? 'Hoạt động' : 'Ngừng hoạt động')
         },
         {
            title: t('actions'),
            key: 'action',
            align: 'center' as 'center',
            width: 120,
            fixed: 'right' as 'right',
            render: (record: any) => (
               <span>
                  {/* <span onClick={() => showDetail(record)} className="gx-link">
                     <InfoCircleOutlined />
                  </span>
                  <Divider type="vertical" /> */}
                  <span onClick={() => showEdit(record)} className="gx-link">
                     <EditOutlined />
                  </span>
                  <Divider type="vertical" />
                  <span onClick={() => handleDelete(record)} className="gx-link">
                     <DeleteOutlined />
                  </span>
               </span>
            )
         }
      ],
      [t, showDetail, showEdit, handleDelete]
   )
   const renderPagination = () => {
      if (isEmpty(rows)) {
         return null
      }

      return (
         <div className="containerPagination">
            <Pagination
               onChange={onChange}
               pageSize={PAGE_SIZE}
               current={offset / PAGE_SIZE + 1}
               showSizeChanger={false}
               total={count}
            />
         </div>
      )
   }

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Khuyến mãi'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined className="gx-link" />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item className="gx-link">{'Khuyến mãi'}</Breadcrumb.Item>
               </Breadcrumb>
            </Col>
            <Col>
               <Button icon={<PlusCircleOutlined />} type="primary" onClick={handleRedirectToCreate}>
                  {t('add_new')}
               </Button>
            </Col>
         </Row>

         <Card style={{ marginTop: '20px' }}>
            <Row gutter={16}>
               <Col span={8}>
                  <Input.Search placeholder={t('search')} allowClear onSearch={handleChangeTextSearch} />
               </Col>
               {/* <Col span={8}>
                  <Select
                     placeholder={'Tất cả loại tài khoản'}
                     style={{ width: '100%' }}
                     allowClear
                     onChange={(v) => setFormSearch({ ...formSearch, role: v })}
                  >
                     <Option value={ROLE_ACCOUNT.USER}>Khách hàng</Option>
                     <Option value={ROLE_ACCOUNT.DRIVER}>Tài xế</Option>
                     <Option value={ROLE_ACCOUNT.ADMIN}>Admin</Option>
                  </Select>
               </Col> */}
               <Col span={4}>
                  <Button icon={<SearchOutlined />} type="primary">
                     {t('search')}
                  </Button>
               </Col>
            </Row>

            <Table style={{ marginTop: 20 }} columns={columns} dataSource={rows} rowKey="id" pagination={false} />
            {renderPagination()}
         </Card>
      </div>
   )
})

export default PromotionScreen
